import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { FlyerData } from './types';

interface FlyerDetailsProps {
  data: FlyerData;
  onEdit?: (field: keyof FlyerData, value: string) => void;
  className?: string;
}

const FlyerDetails: React.FC<FlyerDetailsProps> = ({ data, onEdit, className = '' }) => {
  const [formData, setFormData] = useState({
    headline: data.headline || '',
    description: data.description || '',
    offerDetails: data.offerDetails || '',
    footer: data.footer || '',
    prompt: data.prompt || ''
  });

  // Update formData when data prop changes
  useEffect(() => {
    setFormData({
      headline: data.headline || '',
      description: data.description || '',
      offerDetails: data.offerDetails || '',
      footer: data.footer || '',
      prompt: data.prompt || ''
    });
  }, [data]);

  const handleChange = (field: keyof FlyerData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (onEdit) {
      onEdit(field, value);
    }
  };

  // Auto-resize textarea
  const adjustHeight = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className={`space-y-6 ${className}`}
    >
      {/* Headline */}
      <div>
        <h3 className="text-brand-primary text-sm font-semibold mb-2">Headline</h3>
        <textarea
          value={formData.headline}
          onChange={(e) => {
            handleChange('headline', e.target.value);
            adjustHeight(e);
          }}
          placeholder="Enter headline..."
          className="w-full bg-transparent text-white text-base leading-relaxed border-0 border-b border-transparent hover:border-gray-700/30 focus:border-brand-primary/50 focus:outline-none transition-colors resize-none p-0"
          rows={1}
          style={{ minHeight: '24px' }}
        />
      </div>

      {/* Description */}
      <div>
        <h3 className="text-brand-primary text-sm font-semibold mb-2">Description</h3>
        <textarea
          value={formData.description}
          onChange={(e) => {
            handleChange('description', e.target.value);
            adjustHeight(e);
          }}
          placeholder="Enter description..."
          className="w-full bg-transparent text-white text-base leading-relaxed border-0 border-b border-transparent hover:border-gray-700/30 focus:border-brand-primary/50 focus:outline-none transition-colors resize-none p-0"
          rows={2}
          style={{ minHeight: '48px' }}
        />
      </div>

      {/* Offer Details */}
      <div>
        <h3 className="text-brand-primary text-sm font-semibold mb-2">Offer Details</h3>
        <textarea
          value={formData.offerDetails}
          onChange={(e) => {
            handleChange('offerDetails', e.target.value);
            adjustHeight(e);
          }}
          placeholder="Enter offer details..."
          className="w-full bg-transparent text-white text-base leading-relaxed border-0 border-b border-transparent hover:border-gray-700/30 focus:border-brand-primary/50 focus:outline-none transition-colors resize-none p-0"
          rows={1}
          style={{ minHeight: '24px' }}
        />
      </div>

      {/* Footer */}
      <div>
        <h3 className="text-brand-primary text-sm font-semibold mb-2">Footer</h3>
        <textarea
          value={formData.footer}
          onChange={(e) => {
            handleChange('footer', e.target.value);
            adjustHeight(e);
          }}
          placeholder="Enter footer..."
          className="w-full bg-transparent text-gray-400 text-base leading-relaxed border-0 border-b border-transparent hover:border-gray-700/30 focus:border-brand-primary/50 focus:outline-none transition-colors resize-none p-0"
          rows={1}
          style={{ minHeight: '24px' }}
        />
      </div>
      
      {/* Prompt Section */}
      <div className="pt-4 border-t border-gray-700/50">
        <h3 className="text-brand-primary text-sm font-semibold mb-2">Prompt</h3>
        <textarea
          value={formData.prompt}
          onChange={(e) => {
            handleChange('prompt', e.target.value);
            adjustHeight(e);
          }}
          placeholder="Enter your custom prompt here..."
          className="w-full bg-transparent text-white text-base leading-relaxed border-0 border-b border-transparent hover:border-gray-700/30 focus:border-brand-primary/50 focus:outline-none transition-colors resize-none p-0"
          rows={3}
          style={{ minHeight: '72px' }}
        />
      </div>
    </motion.div>
  );
};

export default FlyerDetails;
